import { clientApi, couponApi } from "@api";
import { enumFilter, initPage } from "@configs";
import {
  IAdvanceSearch,
  ICdicCoupon,
  ICoupon,
  ICouponFamily,
  ICouponFamilySearch,
  ICouponProgram,
  ICouponSearch,
  ICouponService,
  IExportStatusCoupon,
  IFormExportValues,
  IPagination,
  IProgram,
  IService,
  ResendParam,
} from "@interfaces";
import { setLoading } from "@redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { getApiErrorMessage } from "../apiError";
import { useNotify } from "../notifyHook";

export const couponQueryKeys = {
  all: ["coupons"] as const,
  familyLists: () => [...couponQueryKeys.all, "familyList"] as const,
  familyList: (params: ICouponSearch) => [...couponQueryKeys.familyLists(), params] as const,
  familyCoupons: (familyId: string, params: ICouponFamilySearch) =>
    [...couponQueryKeys.all, "familyCoupons", familyId, params] as const,
  cdicLists: () => [...couponQueryKeys.all, "cdicList"] as const,
  cdicList: (programId: string, params: ICouponSearch) =>
    [...couponQueryKeys.cdicLists(), programId, params] as const,
  programs: () => [...couponQueryKeys.all, "programs"] as const,
  programList: (params: ICouponSearch) => [...couponQueryKeys.programs(), "list", params] as const,
  programDetail: (id: string) => [...couponQueryKeys.programs(), "detail", id] as const,
  programNames: () => [...couponQueryKeys.programs(), "activeNames"] as const,
  serviceNames: () => [...couponQueryKeys.all, "serviceNames"] as const,
  searchPrograms: (query: string) => [...couponQueryKeys.all, "searchProgram", query] as const,
  searchServices: (query: string) => [...couponQueryKeys.all, "searchService", query] as const,
  searchFamilies: (serviceId: string) => [...couponQueryKeys.all, "searchFamily", serviceId] as const,
};

const invalidateFamilyLists = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: couponQueryKeys.familyLists() });
};

const invalidateFamilyCoupons = (
  queryClient: ReturnType<typeof useQueryClient>,
  familyId?: string
) => {
  if (familyId) {
    queryClient.invalidateQueries({
      queryKey: [...couponQueryKeys.all, "familyCoupons", familyId],
    });
  } else {
    queryClient.invalidateQueries({
      queryKey: [...couponQueryKeys.all, "familyCoupons"],
    });
  }
};

const invalidateCdicLists = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: couponQueryKeys.cdicLists() });
};

const invalidateProgramLists = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: couponQueryKeys.programs() });
};

const useQueryLoading = (isFetching: boolean) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setLoading(isFetching));
  }, [dispatch, isFetching]);
};

const useQueryError = (isError: boolean, queryError: unknown, fallbackKey: string) => {
  const { t } = useTranslation();
  const { error } = useNotify();
  useEffect(() => {
    if (isError) {
      error(getApiErrorMessage(queryError, t(fallbackKey)));
    }
  }, [isError, queryError, error, t, fallbackKey]);
};

export const useCouponFamilyListPage = () => {
  const [page, setPage] = useState<IPagination>(initPage);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState("");
  const [date, setDate] = useState<string[]>([]);

  const params: ICouponSearch = {
    page: page.page,
    limit: page.limit,
    searchColumn: filters,
    search: text,
    startDate: date[0],
    endDate: date[1],
  };

  const query = useQuery({
    queryKey: couponQueryKeys.familyList(params),
    queryFn: async () => {
      const res = await couponApi.coupons(params);
      return {
        data: res.data?.data as ICoupon[],
        total: res.data?.nTotal as number,
      };
    },
    placeholderData: (previousData) => previousData,
  });

  useQueryLoading(query.isFetching);
  useQueryError(query.isError, query.error, "message.fetch.fail");

  const changeFilter = (value: string) => {
    setText("");
    setFilters(value);
  };

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page,
    filters,
    text,
    date,
    refetch: query.refetch,
    handlePagination: (current: number, size: number) =>
      setPage({ ...page, page: current, limit: size }),
    changeFilter,
    searchText: setText,
    handleClearSearch: () => {
      setText("");
      setFilters("");
    },
  };
};

export const useCouponFamilyCouponsPage = (familyId: string) => {
  const [page, setPage] = useState<IPagination>(initPage);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState("");
  const [date, setDate] = useState<string[]>([]);
  const [advance, setAdvance] = useState<IAdvanceSearch>();

  const params: ICouponFamilySearch = {
    page: page.page,
    limit: page.limit,
    ...(filters && { searchColumn: filters }),
    ...(text && { search: text }),
    ...(date.length > 1 && { startDate: date[0] }),
    ...(date.length > 1 && { endDate: date[1] }),
    ...(advance && advance),
  };

  const query = useQuery({
    queryKey: couponQueryKeys.familyCoupons(familyId, params),
    queryFn: async () => {
      const res = await couponApi.coupon(params, familyId);
      return {
        data: res.data?.data as ICouponFamily[],
        total: res.data?.nTotal as number,
      };
    },
    enabled: Boolean(familyId),
    placeholderData: (previousData) => previousData,
  });

  useQueryLoading(query.isFetching);
  useQueryError(query.isError, query.error, "message.fetch.fail");

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page,
    filters,
    text,
    date,
    advance,
    refetch: query.refetch,
    setAdvance,
    handleDateFilter: (_dates: null | (Dayjs | null)[], dateStrings: string[]) =>
      setDate(dateStrings),
    handlePagination: (current: number, size: number) =>
      setPage({ ...page, page: current, limit: size }),
    changeFilter: (value: string) => {
      setFilters(value);
      setText("");
    },
    searchText: setText,
    handleClearSearch: () => {
      setText("");
      setFilters("");
    },
    handleClearDate: () => setDate([]),
  };
};

export const useCdicCouponListPage = (programId: string) => {
  const [page, setPage] = useState<IPagination>(initPage);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState("");
  const [date, setDate] = useState<string[]>([]);

  const params: ICouponSearch = {
    page: page.page,
    limit: page.limit,
    searchColumn: filters,
    search: text,
    startDate: date[0],
    endDate: date[1],
  };

  const query = useQuery({
    queryKey: couponQueryKeys.cdicList(programId, params),
    queryFn: async () => {
      const res = await couponApi.cdicCoupons(programId, params);
      return {
        data: res.data?.data as ICdicCoupon[],
        total: res.data?.nTotal as number,
      };
    },
    enabled: Boolean(programId),
    placeholderData: (previousData) => previousData,
  });

  useQueryLoading(query.isFetching);
  useQueryError(query.isError, query.error, "message.fetch.fail");

  const changeFilter = (value: string) => {
    setText("");
    setFilters(value);
  };

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page,
    filters,
    text,
    date,
    refetch: query.refetch,
    handlePagination: (current: number, size: number) =>
      setPage({ ...page, page: current, limit: size }),
    changeFilter,
    searchText: setText,
    handleClearSearch: () => {
      setText("");
      setFilters("");
    },
  };
};

export const useProgramListPage = () => {
  const [page, setPage] = useState<IPagination>(initPage);
  const [filters, setFilters] = useState<enumFilter | string>("");
  const [text, setText] = useState("");
  const [date, setDate] = useState<string[]>([]);

  const params: ICouponSearch = {
    page: page.page,
    limit: page.limit,
    searchColumn: filters,
    search: text,
    startDate: date[0],
    endDate: date[1],
  };

  const query = useQuery({
    queryKey: couponQueryKeys.programList(params),
    queryFn: async () => {
      const res = await couponApi.getProgramList(params);
      return {
        data: res.data?.data as ICouponProgram[],
        total: res.data?.nTotal as number,
      };
    },
    placeholderData: (previousData) => previousData,
  });

  useQueryLoading(query.isFetching);
  useQueryError(query.isError, query.error, "message.fetch.fail");

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    page,
    filters,
    text,
    date,
    refetch: query.refetch,
    handlePagination: (current: number, size: number) =>
      setPage({ ...page, page: current, limit: size }),
    changeFilter: (value: string) => {
      setText("");
      setFilters(value);
    },
    searchText: setText,
    handleClearSearch: () => {
      setText("");
      setFilters("");
    },
  };
};

export const useProgramDetail = (id?: string) => {
  const query = useQuery({
    queryKey: couponQueryKeys.programDetail(id ?? ""),
    queryFn: async () => {
      const res = await couponApi.getProgram(id as string);
      return res.data as ICouponProgram;
    },
    enabled: Boolean(id),
  });

  useQueryLoading(query.isFetching);
  useQueryError(query.isError, query.error, "message.fetch.fail");

  return query;
};

export const useServiceNames = () =>
  useQuery({
    queryKey: couponQueryKeys.serviceNames(),
    queryFn: async () => {
      const res = await couponApi.searchServiceNames();
      return res.data as ICouponService[];
    },
  });

export const useActiveProgramNames = () =>
  useQuery({
    queryKey: couponQueryKeys.programNames(),
    queryFn: async () => {
      const res = await couponApi.searchProgramName();
      return res.data as ICouponProgram[];
    },
  });

export const useSearchPrograms = (query: string) =>
  useQuery({
    queryKey: couponQueryKeys.searchPrograms(query),
    queryFn: async () => {
      const res = await couponApi.searchProgram(query);
      return res.data?.data as IProgram[];
    },
    enabled: Boolean(query),
  });

export const useSearchServices = (query: string) =>
  useQuery({
    queryKey: couponQueryKeys.searchServices(query),
    queryFn: async () => {
      const res = await couponApi.searchServiceName(query);
      return res.data?.data as IService[];
    },
    enabled: Boolean(query),
  });

export const useSearchCouponFamilies = (serviceId: string) =>
  useQuery({
    queryKey: couponQueryKeys.searchFamilies(serviceId),
    queryFn: async () => {
      const res = await couponApi.searchFamilyByService(serviceId);
      return res.data?.data as ICoupon[];
    },
    enabled: Boolean(serviceId),
  });

export const useCreateCouponFamily = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICoupon) => couponApi.create(payload),
    onSuccess: () => invalidateFamilyLists(queryClient),
  });
};

export const useUpdateCouponFamily = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, id }: { payload: ICoupon; id: string }) =>
      couponApi.updateFamily(payload, id),
    onSuccess: () => invalidateFamilyLists(queryClient),
  });
};

export const useUpdateCouponStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, id }: { payload: ICouponFamily; id: string }) =>
      couponApi.updateStatus(payload, id),
    onSuccess: () => {
      invalidateFamilyLists(queryClient);
      invalidateFamilyCoupons(queryClient);
    },
  });
};

export const useResendCouponSms = () =>
  useMutation({
    mutationFn: (params: ResendParam) => couponApi.resendSms(params),
  });

export const useGenerateCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICoupon) => couponApi.generate(payload),
    onSuccess: () => {
      invalidateFamilyLists(queryClient);
      invalidateFamilyCoupons(queryClient);
    },
  });
};

export const useUpdateCdicCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ payload, id }: { payload: ICdicCoupon; id: string }) =>
      couponApi.updateCdicCoupons(payload, id),
    onSuccess: () => {
      invalidateCdicLists(queryClient);
      invalidateProgramLists(queryClient);
    },
  });
};

export const useResendCdicSms = () =>
  useMutation({
    mutationFn: (params: ResendParam) => couponApi.resendSmsCdic(params),
  });

export const useGenerateCdicCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICdicCoupon) => couponApi.generateCdic(payload),
    onSuccess: () => invalidateCdicLists(queryClient),
  });
};

export const useCreateProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICouponProgram) => couponApi.createProgram(payload),
    onSuccess: () => invalidateProgramLists(queryClient),
  });
};

export const useUpdateProgram = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ICouponProgram }) =>
      couponApi.updateProgram(id, payload),
    onSuccess: (_data, { id }) => {
      invalidateProgramLists(queryClient);
      queryClient.invalidateQueries({ queryKey: couponQueryKeys.programDetail(id) });
    },
  });
};

export const useCreateService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ICouponService) => couponApi.createService(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponQueryKeys.serviceNames() });
    },
  });
};

export const useDeleteService = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => couponApi.deleteService(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponQueryKeys.serviceNames() });
    },
  });
};

export const useLookupClientByPhone = () =>
  useMutation({
    mutationFn: (phone: string) => clientApi.clientPhone(phone),
  });

export const useExportCoupons = () =>
  useMutation({
    mutationFn: (params: IFormExportValues) => couponApi.exportCoupons(params),
  });

export const useExportActiveCoupons = () =>
  useMutation({
    mutationFn: ({ id, params }: { id: string; params: ICouponSearch }) =>
      couponApi.exportActiveExcel(id, params),
  });

export const useExportCdicStatus = () =>
  useMutation({
    mutationFn: ({ id, params }: { id: string; params: IExportStatusCoupon }) =>
      couponApi.exportStatusCdicExcel(id, params),
  });

export const useExportActiveCdic = () =>
  useMutation({
    mutationFn: ({ id, params }: { id: string; params: ICouponSearch }) =>
      couponApi.exportActiveCdicExcel(id, params),
  });
